import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  TECH_CALENDAR_LOCATION,
  TECH_CALENDAR_TECHNICIANS,
  TECH_CALENDAR_WEEK_OFFSET,
  TECH_CALENDAR_VIEW_MODE,
  TECH_CALENDAR_WO_STATUSES,
  TECH_CALENDAR_WO_DOC_STATUSES,
  TECH_CALENDAR_COLLAPSE_SCHEDULE_ITEMS,
  TECH_CALENDAR_WEEK_DAY_RANGE,
} from "../common/constants/filterParamConstants";
import { SettingsLocation, ApplicationUser } from "../../models/lookupTypes";
import { getCache } from "../common/Utils/cacheUtil";
import type { WeekDayRange } from "../technicianCalendar/lib/types";

/**
 * Zustand Store for Technician Calendar - Filter State Only
 * 
 * This store is responsible for:
 * 1. Filter state persistence (selectedLocationIds, selectedTechnicianIds, weekOffset, viewMode)
 * 2. Dropdown data for filters (locations, technicians)
 * 
 * Scheduling data (businessHours, holidays, employeeSchedules, employeeScheduleExceptions)
 * is managed by schedule.state in page.tsx, which is the single source of truth for
 * capacity calculations.
 */

interface TechnicianCalendarState {
  // Dropdown data (loaded from cache for filter dropdowns)
  locations: SettingsLocation[];
  techniciansForFilters: ApplicationUser[];

  // Filter state (persisted to localStorage)
  selectedLocationIds: string[]; // Multi-select: ["0"] = All, or array of location identity strings
  selectedTechnicianIds: string[];
  selectedWoStatusIds: string[];
  /** Document status strings (InvoiceHeader.Status); ["0"] = All */
  selectedWoDocStatusIds: string[];
  weekOffset: number;
  viewMode: "day" | "week" | "month";
  scheduleItemView: "active" | "completed" | "archived";
  /** When true, schedule item cards show only WO number and action buttons */
  collapseScheduleItems: boolean;
  /** Week view columns: full week or weekdays only */
  weekDayRange: WeekDayRange;

  // Actions for dropdown data
  setLocations: (locations: SettingsLocation[]) => void;
  setTechniciansForFilters: (technicians: ApplicationUser[]) => void;

  // Actions for filter state
  setSelectedLocationIds: (locationIds: string[]) => void;
  setSelectedTechnicianIds: (technicianIds: string[]) => void;
  setSelectedWoStatusIds: (statusIds: string[]) => void;
  setSelectedWoDocStatusIds: (statusIds: string[]) => void;
  setWeekOffset: (offset: number) => void;
  setViewMode: (mode: "day" | "week" | "month") => void;
  setScheduleItemView: (view: "active" | "completed" | "archived") => void;
  setCollapseScheduleItems: (collapsed: boolean) => void;
  setWeekDayRange: (range: WeekDayRange) => void;
  resetFilters: () => void;
}

// Helper functions for localStorage persistence
const getUsername = (): string => {
  return localStorage.getItem("username") || "";
};

const loadPersistedLocations = (
  _defaultLocation: number | null,
): string[] => {
  const username = getUsername();
  if (!username) return ["0"]; // Default to "All"
  const stored = localStorage.getItem(`${TECH_CALENDAR_LOCATION}_${username}`);
  if (stored) {
    // Handle comma-separated string format (like FilterPanel)
    if (stored.includes(",")) {
      return stored
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    // Handle single value
    if (stored.trim().length > 0) {
      return [stored.trim()];
    }
  }
  return ["0"]; // Default to "All"
};

const loadPersistedTechnicians = (): string[] => {
  const username = getUsername();
  if (!username) return ["0"]; // Default to "All" on page load
  const stored = localStorage.getItem(
    `${TECH_CALENDAR_TECHNICIANS}_${username}`,
  );
  if (stored) {
    // Handle comma-separated string format (like FilterPanel)
    if (stored.includes(",")) {
      return stored
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    // Handle single value
    if (stored.trim().length > 0) {
      return [stored.trim()];
    }
  }
  return ["0"]; // Default to "All" on page load
};

const loadPersistedWoDocStatuses = (): string[] => {
  const username = getUsername();
  if (!username) return ["0"];
  const stored = localStorage.getItem(
    `${TECH_CALENDAR_WO_DOC_STATUSES}_${username}`,
  );
  if (stored) {
    if (stored.includes(",")) {
      return stored
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    if (stored.trim().length > 0) {
      return [stored.trim()];
    }
  }
  return ["0"];
};

const loadPersistedWeekOffset = (): number => {
  const username = getUsername();
  if (!username) return 0;
  const stored = localStorage.getItem(
    `${TECH_CALENDAR_WEEK_OFFSET}_${username}`,
  );
  if (stored) {
    const parsed = parseInt(stored, 10);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const loadPersistedWoStatuses = (): string[] => {
  const username = getUsername();
  if (!username) return ["0"]; // Default to "All" on page load
  const stored = localStorage.getItem(
    `${TECH_CALENDAR_WO_STATUSES}_${username}`,
  );
  if (stored) {
    // Handle comma-separated string format (like FilterPanel)
    if (stored.includes(",")) {
      return stored
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    // Handle single value
    if (stored.trim().length > 0) {
      return [stored.trim()];
    }
  }
  return ["0"]; // Default to "All" on page load
};

const loadPersistedViewMode = (): "day" | "week" | "month" => {
  const username = getUsername();
  if (!username) return "week";
  const stored = localStorage.getItem(`${TECH_CALENDAR_VIEW_MODE}_${username}`);
  return stored === "day" || stored === "week" || stored === "month" ? stored : "week";
};

const loadPersistedCollapseScheduleItems = (): boolean => {
  const username = getUsername();
  if (!username) return false;
  return (
    localStorage.getItem(
      `${TECH_CALENDAR_COLLAPSE_SCHEDULE_ITEMS}_${username}`,
    ) === "true"
  );
};

const loadPersistedWeekDayRange = (): WeekDayRange => {
  const username = getUsername();
  if (!username) return "sun-sat";
  const stored = localStorage.getItem(
    `${TECH_CALENDAR_WEEK_DAY_RANGE}_${username}`,
  );
  return stored === "mon-fri" ? "mon-fri" : "sun-sat";
};

// Get user's default location
const getUserDefaultLocation = (): number | null => {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      return user?.defaultLocationIdentity ?? null;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

// Load dropdown data from cache (for filter dropdowns only)
const loadCachedLocations = (): SettingsLocation[] => {
  const username = getUsername();
  if (!username) return [];
  const MAX_CACHE_AGE = 1000 * 60 * 60 * 24; // 24 hours
  const cached = getCache<SettingsLocation[]>(
    "settingsLocations",
    username,
    MAX_CACHE_AGE,
  );
  return cached || [];
};

const loadCachedTechnicians = (): ApplicationUser[] => {
  const username = getUsername();
  if (!username) return [];
  const MAX_CACHE_AGE = 1000 * 60 * 60 * 24; // 24 hours
  const cached = getCache<ApplicationUser[]>(
    "techniciansForFilters",
    username,
    MAX_CACHE_AGE,
  );
  return cached || [];
};

// Create the Zustand store
export const useTechnicianCalendarStore = create<TechnicianCalendarState>()(
  devtools((set, get) => {
    const defaultLocation = getUserDefaultLocation();

    return {
      // Initial state - load dropdown data from cache, filter state from localStorage
      locations: loadCachedLocations(),
      techniciansForFilters: loadCachedTechnicians(),
      selectedLocationIds: loadPersistedLocations(defaultLocation),
      selectedTechnicianIds: loadPersistedTechnicians(),
      selectedWoStatusIds: loadPersistedWoStatuses(),
      selectedWoDocStatusIds: loadPersistedWoDocStatuses(),
      weekOffset: loadPersistedWeekOffset(),
      viewMode: loadPersistedViewMode(),
      scheduleItemView: "active",
      collapseScheduleItems: loadPersistedCollapseScheduleItems(),
      weekDayRange: loadPersistedWeekDayRange(),

      // Actions for dropdown data
      setLocations: (locations) => set({ locations }),
      setTechniciansForFilters: (technicians) =>
        set({ techniciansForFilters: technicians }),

      // Actions for filter state
      setSelectedLocationIds: (locationIds) => {
        const username = getUsername();
        if (username) {
          // Store as comma-separated string (like FilterPanel)
          if (
            locationIds.length === 0 ||
            (locationIds.length === 1 && locationIds[0] === "0")
          ) {
            localStorage.setItem(
              `${TECH_CALENDAR_LOCATION}_${username}`,
              "0",
            );
          } else {
            localStorage.setItem(
              `${TECH_CALENDAR_LOCATION}_${username}`,
              locationIds.join(","),
            );
          }
        }
        set({ selectedLocationIds: locationIds });
      },

      setSelectedTechnicianIds: (technicianIds) => {
        const username = getUsername();
        if (username) {
          // Store as comma-separated string (like FilterPanel)
          if (
            technicianIds.length === 0 ||
            (technicianIds.length === 1 && technicianIds[0] === "0")
          ) {
            localStorage.setItem(
              `${TECH_CALENDAR_TECHNICIANS}_${username}`,
              "0",
            );
          } else {
            localStorage.setItem(
              `${TECH_CALENDAR_TECHNICIANS}_${username}`,
              technicianIds.join(","),
            );
          }
        }
        set({ selectedTechnicianIds: technicianIds });
      },

      setSelectedWoStatusIds: (statusIds) => {
        const username = getUsername();
        if (username) {
          // Store as comma-separated string (like FilterPanel)
          if (
            statusIds.length === 0 ||
            (statusIds.length === 1 && statusIds[0] === "0")
          ) {
            localStorage.setItem(
              `${TECH_CALENDAR_WO_STATUSES}_${username}`,
              "0",
            );
          } else {
            localStorage.setItem(
              `${TECH_CALENDAR_WO_STATUSES}_${username}`,
              statusIds.join(","),
            );
          }
        }
        set({ selectedWoStatusIds: statusIds });
      },

      setSelectedWoDocStatusIds: (statusIds) => {
        const username = getUsername();
        if (username) {
          if (
            statusIds.length === 0 ||
            (statusIds.length === 1 && statusIds[0] === "0")
          ) {
            localStorage.setItem(
              `${TECH_CALENDAR_WO_DOC_STATUSES}_${username}`,
              "0",
            );
          } else {
            localStorage.setItem(
              `${TECH_CALENDAR_WO_DOC_STATUSES}_${username}`,
              statusIds.join(","),
            );
          }
        }
        set({ selectedWoDocStatusIds: statusIds });
      },

      setWeekOffset: (offset) => {
        const username = getUsername();
        if (username) {
          localStorage.setItem(
            `${TECH_CALENDAR_WEEK_OFFSET}_${username}`,
            offset.toString(),
          );
        }
        set({ weekOffset: offset });
      },

      setViewMode: (mode) => {
        const username = getUsername();
        if (username) {
          localStorage.setItem(`${TECH_CALENDAR_VIEW_MODE}_${username}`, mode);
        }
        set({ viewMode: mode });
      },

      setScheduleItemView: (view) => {
        set({ scheduleItemView: view });
      },

      setCollapseScheduleItems: (collapsed) => {
        const username = getUsername();
        if (username) {
          localStorage.setItem(
            `${TECH_CALENDAR_COLLAPSE_SCHEDULE_ITEMS}_${username}`,
            `${collapsed}`,
          );
        }
        set({ collapseScheduleItems: collapsed });
      },

      setWeekDayRange: (range) => {
        const username = getUsername();
        if (username) {
          localStorage.setItem(
            `${TECH_CALENDAR_WEEK_DAY_RANGE}_${username}`,
            range,
          );
        }
        set({ weekDayRange: range });
      },

      resetFilters: () => {
        const username = getUsername();

        // Clear localStorage - default to "All" for locations
        if (username) {
          localStorage.setItem(`${TECH_CALENDAR_LOCATION}_${username}`, "0");
          localStorage.setItem(`${TECH_CALENDAR_TECHNICIANS}_${username}`, "0");
          localStorage.setItem(`${TECH_CALENDAR_WO_STATUSES}_${username}`, "0");
          localStorage.setItem(
            `${TECH_CALENDAR_WO_DOC_STATUSES}_${username}`,
            "0",
          );
          localStorage.setItem(`${TECH_CALENDAR_WEEK_OFFSET}_${username}`, "0");
          localStorage.setItem(
            `${TECH_CALENDAR_VIEW_MODE}_${username}`,
            "week",
          );
        }

        set({
          selectedLocationIds: ["0"], // Default to "All"
          selectedTechnicianIds: ["0"],
          selectedWoStatusIds: ["0"],
          selectedWoDocStatusIds: ["0"],
          weekOffset: 0,
          viewMode: "week",
          scheduleItemView: "active",
        });
      },
    };
  }),
);

// Expose store to window for debugging (only in development)
if (typeof window !== "undefined" && import.meta.env.DEV) {
  (window as any).__technicianCalendarStore = useTechnicianCalendarStore;
  (window as any).__getTechnicianCalendarState = () => {
    return useTechnicianCalendarStore.getState();
  };
  console.log("Technician Calendar Store exposed to window.__technicianCalendarStore");
  console.log("Use window.__getTechnicianCalendarState() to see current state");
}
